import React, { useState, useEffect } from 'react';
import { IoClose, IoSaveOutline, IoReceipt } from 'react-icons/io5';
import { db } from '../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { collection, addDoc, Timestamp, doc, updateDoc, writeBatch, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { postLedgerEntry, getAccountMap, ACCOUNTS } from '../utils/accountingUtils';
import moment from 'moment';
import './ClasificarGastoModal.css';

// Fallbacks
// eslint-disable-next-line no-unused-vars
const DEFAULT_EXPENSE_CATEGORIES = ['Mantenimiento General', 'Servicios', 'Otros'];
// eslint-disable-next-line no-unused-vars
const DEFAULT_INCOME_CATEGORIES = ['Intereses Bancarios', 'Devoluciones', 'Otros'];

function ClasificarGastoModal({ mov, onClose, onSuccess }) {
    const { userData } = useAuth();
    const isIncome = mov.monto > 0;

    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(true);

    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [selectedAccountName, setSelectedAccountName] = useState('');

    const [activeTab, setActiveTab] = useState('nuevo'); // 'nuevo' | 'pagar_cxp'
    const [provisions, setProvisions] = useState([]);
    const [selectedProvision, setSelectedProvision] = useState(null);

    // Legacy mapping or just free text desc
    const [description, setDescription] = useState(mov.descripcion || '');
    const [provider, setProvider] = useState(mov.referencia || '');
    const [loading, setLoading] = useState(false);

    const [providerList, setProviderList] = useState([]);

    useEffect(() => {
        fetchAccounts();
        if (userData?.condominioId) fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchProviders = async () => {
        try { // Safe fetch
            const q = query(collection(db, "condominio_proveedores"), where("condominioId", "==", userData.condominioId));
            const snap = await getDocs(q);
            setProviderList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.warn("Could not fetch providers for autocomplete", e);
        }
    };

    useEffect(() => {
        if (activeTab === 'pagar_cxp') {
            fetchProvisions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const fetchAccounts = async () => {
        if (!userData?.condominioId) return;
        setLoadingAccounts(true);
        try {
            const q = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), orderBy("numero", "asc"));
            const snap = await getDocs(q);
            const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            setAccounts(fetched.filter(a =>
                isIncome ? (a.tipo === 'INGRESO' || a.tipo === 'PASIVO') : (a.tipo === 'GASTO' || a.tipo === 'ACTIVO')
            ));
        } catch (e) {
            console.error("Error fetching accounts", e);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const fetchProvisions = async () => {
        if (!userData?.condominioId) return;
        // Fetch UNPAID expenses (estatus: 'pendiente') that are EXPENSES (tipo: 'egreso')
        try {
            const q = query(
                collection(db, "gastos"),
                where("condominioId", "==", userData.condominioId),
                where("estatus", "==", "pendiente"),
                where("tipo", "==", "egreso")
            );
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Sort by due date (oldest first)
            list.sort((a, b) => (a.fechaVencimiento?.seconds || 0) - (b.fechaVencimiento?.seconds || 0));
            setProvisions(list);
        } catch (error) {
            console.error("Error fetching provisions:", error);
        }
    };

    const handleSaveNew = async () => {
        if (!selectedAccountId && !selectedAccountName) {
            alert("Por favor selecciona una categoría/cuenta.");
            return;
        }

        setLoading(true);
        try {
            const batch = writeBatch(db);

            // 1. Create Record (Gasto or Ingreso)
            const docRef = doc(collection(db, 'gastos'));

            batch.set(docRef, {
                condominioId: userData.condominioId,
                movimientoBancarioId: mov.id,
                monto: Math.abs(mov.monto),
                tipo: isIncome ? 'ingreso' : 'egreso',
                fecha: mov.fecha,
                fechaRegistro: Timestamp.now(),
                estatus: 'pagado', // It's paid immediately as it comes from bank
                fechaPago: mov.fecha,

                // Store Accounting Info
                cuentaContableId: selectedAccountId,
                categoria: selectedAccountName,

                descripcion: description,
                proveedor: provider,
                registradoPor: userData?.uid || userData?.id || 'admin'
            });

            // 2. Update Bank Movement Status
            const movRef = doc(db, 'movimientos_bancarios', mov.id);
            batch.update(movRef, {
                estatus: 'clasificado',
                categoria: selectedAccountName,
                cuentaContableId: selectedAccountId,
                gastoId: docRef.id,
                tipoClasificacion: isIncome ? 'ingreso' : 'egreso',
                fechaConciliacion: Timestamp.now()
            });

            await batch.commit();

            // 3. Post to Ledger
            await postLedgerEntryForNew(docRef.id);

            alert(`${isIncome ? 'Ingreso' : 'Gasto'} clasificado correctamente.`);
            if (onSuccess) onSuccess();
            onClose();

        } catch (error) {
            console.error("Error classifying:", error);
            alert("Error al guardar.");
        } finally {
            setLoading(false);
        }
    };

    const handlePayProvision = async () => {
        if (!selectedProvision) {
            alert("Selecciona una factura para pagar.");
            return;
        }

        const paymentAmount = Math.abs(mov.monto);

        // Validation: Verify if payment exceeds remaining balance (optional but good UX)
        const currentPaid = selectedProvision.montoPagado || 0;
        const remaining = selectedProvision.monto - currentPaid;

        if (paymentAmount > (remaining + 0.01)) {
            if (!window.confirm(`El monto del pago ($${paymentAmount}) es MAYOR al saldo pendiente ($${remaining}). ¿Deseas continuar y registrarlo de todas formas?`)) {
                return;
            }
        }

        if (window.confirm(`¿Confirmas vincular este pago de $${new Intl.NumberFormat('es-MX').format(paymentAmount)} a la factura de ${selectedProvision.proveedor}?`)) {
            setLoading(true);
            try {
                const batch = writeBatch(db);

                // 1. Calculate New Status & Paid Amount
                const newTotalPaid = currentPaid + paymentAmount;
                const isFullyPaid = newTotalPaid >= (selectedProvision.monto - 0.01);

                // 2. Update Gasto (Provision)
                const gastoRef = doc(db, 'gastos', selectedProvision.id);
                // Append this payment to history (if we want to track individual payments in array)
                // For MVP, accumulating montoPagado is crucial.

                batch.update(gastoRef, {
                    estatus: isFullyPaid ? 'pagado' : 'pendiente', // Remains 'pendiente' if partial
                    fechaPago: isFullyPaid ? mov.fecha : (selectedProvision.fechaPago || null), // Set date only if fully paid? Or last payment date? Let's treat fechaPago as 'Last Payment Date'
                    montoPagado: newTotalPaid,
                    movimientoBancarioId: mov.id // Note: This overrides previous link. Ideally strict link is 1-to-1 or 1-to-many. 
                    // To support "Show me the payment for this Invoice", we might need an array of payment IDs if multiple. 
                    // But for now, let's just track the financial balance.
                });

                // 3. Update Bank Movement -> Linked
                const movRef = doc(db, 'movimientos_bancarios', mov.id);
                batch.update(movRef, {
                    estatus: 'clasificado',
                    tipoClasificacion: 'pago_provision',
                    gastoId: selectedProvision.id,
                    categoria: 'Pago a Proveedor',
                    fechaConciliacion: Timestamp.now()
                });

                await batch.commit();

                // 4. Post Ledger Entry (Debit Liability, Credit Asset) - Always full amount of THIS payment
                await postLedgerEntryForPayment(selectedProvision, paymentAmount);

                alert("Pago vinculado correctamente.");
                if (onSuccess) onSuccess();
                onClose();

            } catch (error) {
                console.error("Error paying provision:", error);
                alert("Error al vincular el pago.");
            } finally {
                setLoading(false);
            }
        }
    };

    const postLedgerEntryForNew = async (gastoId) => {
        try {
            const accountMap = await getAccountMap(userData.condominioId);
            const bankAccountId = accountMap[ACCOUNTS.BANCOS];

            if (bankAccountId) {
                let detalles = [];
                if (isIncome) {
                    detalles = [
                        { cuentaId: bankAccountId, debe: parseFloat(Math.abs(mov.monto)), haber: 0 },
                        { cuentaId: selectedAccountId, debe: 0, haber: parseFloat(Math.abs(mov.monto)) }
                    ];
                } else {
                    detalles = [
                        { cuentaId: selectedAccountId, debe: parseFloat(Math.abs(mov.monto)), haber: 0 },
                        { cuentaId: bankAccountId, debe: 0, haber: parseFloat(Math.abs(mov.monto)) }
                    ];
                }

                await postLedgerEntry(userData.condominioId, {
                    fecha: mov.fecha,
                    concepto: `Clasificado: ${description || selectedAccountName}`,
                    referenciaId: gastoId,
                    tipoReferencia: isIncome ? 'INGRESO_CLASIFICADO' : 'GASTO',
                    detalles: detalles
                });
            }
        } catch (ledgerError) {
            console.error("Error posting ledger entry:", ledgerError);
        }
    };

    const postLedgerEntryForPayment = async (provision) => {
        try {
            const accountMap = await getAccountMap(userData.condominioId);
            const bankAccountId = accountMap[ACCOUNTS.BANCOS];
            const providerAccountId = accountMap[ACCOUNTS.PROVEEDORES]; // 2010

            if (bankAccountId && providerAccountId) {
                // Debit 2010 (Decrease Liability), Credit 1020 (Decrease Asset)
                const detalles = [
                    { cuentaId: providerAccountId, debe: parseFloat(Math.abs(mov.monto)), haber: 0 },
                    { cuentaId: bankAccountId, debe: 0, haber: parseFloat(Math.abs(mov.monto)) }
                ];

                await postLedgerEntry(userData.condominioId, {
                    fecha: mov.fecha,
                    concepto: `Pago Factura: ${provision.proveedor} (${provision.descripcion || ''})`,
                    referenciaId: provision.id,
                    tipoReferencia: 'PAGO_PROVEEDOR',
                    detalles: detalles
                });
            }
        } catch (ledgerError) {
            console.error("Error posting ledger entry for payment:", ledgerError);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
                <div className="modal-header">
                    <h3><IoReceipt /> {isIncome ? 'Clasificar Ingreso' : 'Clasificar Movimiento'}</h3>
                    <button onClick={onClose} className="close-btn"><IoClose /></button>
                </div>

                {/* TABS - Only for Expenses (Egresos) */}
                {!isIncome && (
                    <div className="modal-tabs" style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: '20px' }}>
                        <button
                            className={`tab-btn ${activeTab === 'nuevo' ? 'active' : ''}`}
                            onClick={() => setActiveTab('nuevo')}
                            style={{ flex: 1, padding: '10px', border: 'none', background: activeTab === 'nuevo' ? '#fff' : '#f9fafb', borderBottom: activeTab === 'nuevo' ? '2px solid #2563eb' : 'none', fontWeight: activeTab === 'nuevo' ? 'bold' : 'normal', cursor: 'pointer' }}
                        >
                            Nuevo Gasto
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'pagar_cxp' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pagar_cxp')}
                            style={{ flex: 1, padding: '10px', border: 'none', background: activeTab === 'pagar_cxp' ? '#fff' : '#f9fafb', borderBottom: activeTab === 'pagar_cxp' ? '2px solid #2563eb' : 'none', fontWeight: activeTab === 'pagar_cxp' ? 'bold' : 'normal', cursor: 'pointer' }}
                        >
                            Pagar Factura (CxP)
                        </button>
                    </div>
                )}

                <div className="modal-body">
                    <div style={{
                        background: isIncome ? '#dcfce7' : '#fee2e2',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: `1px solid ${isIncome ? '#86efac' : '#fca5a5'}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <div>
                            <div style={{ fontSize: '0.9em', color: isIncome ? '#14532d' : '#7f1d1d' }}>
                                Movimiento Bancario {moment(mov.fecha.toDate()).format('DD/MMM')}:
                            </div>
                            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: isIncome ? '#16a34a' : '#b91c1c' }}>
                                ${new Intl.NumberFormat('es-MX').format(Math.abs(mov.monto))}
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#555' }}>
                                {mov.descripcion}
                            </div>
                        </div>
                    </div>

                    {activeTab === 'nuevo' ? (
                        <>
                            <div className="form-group">
                                <label>Cuenta Contable / Categoría</label>
                                {loadingAccounts ? (
                                    <p>Cargando catálogo...</p>
                                ) : (
                                    <select
                                        value={selectedAccountId}
                                        onChange={e => {
                                            const acc = accounts.find(a => a.id === e.target.value);
                                            setSelectedAccountId(e.target.value);
                                            setSelectedAccountName(acc ? `${acc.numero} - ${acc.nombre}` : '');
                                        }}
                                        className="modal-input"
                                    >
                                        <option value="">Selecciona...</option>
                                        {accounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>
                                                {acc.numero} - {acc.nombre}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Descripción / Notas</label>
                                <input
                                    type="text"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    className="modal-input"
                                />
                            </div>

                            <div className="form-group">
                                <label>{isIncome ? 'Origen / Cliente' : 'Proveedor / Beneficiario'}</label>
                                {/* Provider Selection Datalist */}
                                <input
                                    list="provider-suggestions-class"
                                    type="text"
                                    value={provider}
                                    onChange={e => setProvider(e.target.value)}
                                    className="modal-input"
                                    placeholder="Escribe o selecciona..."
                                />
                                <datalist id="provider-suggestions-class">
                                    {providerList.map(p => (
                                        <option key={p.id} value={p.nombre} />
                                    ))}
                                </datalist>
                                {/* Quick Add Provider Button logic could go here, but kept simple for now */}
                            </div>

                            <div className="modal-actions" style={{ marginTop: '20px' }}>
                                <button className="secondary-btn" onClick={onClose} disabled={loading}>Cancelar</button>
                                <button
                                    className="primary-btn"
                                    style={{ background: isIncome ? '#10b981' : '#dc2626' }}
                                    onClick={handleSaveNew}
                                    disabled={loading}
                                >
                                    {loading ? 'Guardando...' : <><IoSaveOutline /> Guardar Gasto Nuevo</>}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="tab-content fade-in">
                            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
                                Selecciona la factura pendiente que estás pagando con este retiro.
                            </p>

                            {provisions.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                                    No tienes facturas pendientes de pago.
                                </div>
                            ) : (
                                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                                    {provisions.map(p => {
                                        const paid = p.montoPagado || 0;
                                        const remaining = p.monto - paid;
                                        return (
                                            <div
                                                key={p.id}
                                                onClick={() => setSelectedProvision(p)}
                                                style={{
                                                    padding: '12px',
                                                    borderBottom: '1px solid #eee',
                                                    cursor: 'pointer',
                                                    background: selectedProvision?.id === p.id ? '#eff6ff' : '#fff',
                                                    borderLeft: selectedProvision?.id === p.id ? '4px solid #2563eb' : '4px solid transparent'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <strong>{p.proveedor}</strong>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
                                                            Saldo: ${new Intl.NumberFormat('es-MX').format(remaining)}
                                                        </div>
                                                        {paid > 0 && <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>(Abonado: ${new Intl.NumberFormat('es-MX').format(paid)})</div>}
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                                    <span>{p.descripcion}</span>
                                                    <span style={{ color: p.fechaVencimiento?.seconds < Timestamp.now().seconds ? '#dc2626' : '#666' }}>
                                                        Vence: {p.fechaVencimiento ? moment(p.fechaVencimiento.toDate()).format('DD/MMM') : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            <div className="modal-actions" style={{ marginTop: '20px' }}>
                                <button className="secondary-btn" onClick={onClose} disabled={loading}>Cancelar</button>
                                <button
                                    className="primary-btn"
                                    onClick={handlePayProvision}
                                    disabled={loading || !selectedProvision}
                                    style={{ background: '#2563eb' }}
                                >
                                    {loading ? 'Vinculando...' : 'Vincular Pago'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ClasificarGastoModal;
